import type { SessionChunk } from 'features/script/script-model'
import { chunkEnd } from 'presenter-mode/model/agenda-clock'
import { LobbyMoment } from './lobby-moment'

/**
 * What the in-person lobby says, worked out from plain values - pure, so the
 * rules are tested without rendering. The lobby is one screen with two
 * moments (before the start, a break) for two audiences:
 *
 * - the trainer runs the room: a checklist before the start, the Start or End
 *   break action, how many people are in;
 * - everyone else - participants on a laptop or a phone, and the projector,
 *   which is the room's own wall - waits, and is told the screen changes by
 *   itself.
 *
 * Every line is a message key with its values; the screen translates them.
 * Anything that depends on the user (clock format, language) is formatted by
 * the caller and comes in as text.
 */

export type LobbyRole = 'trainer' | 'participant'

/**
 * Presenter mode as the trainer's lobby sees it: not offered here (no second
 * screen to present to, or another role), offered, or on.
 */
export type PresenterStatus = 'unavailable' | 'idle' | 'running'

/** People in the room against the people the training invited. */
export type Attendance = { present: number; expected: number }

export type Message = {
	key: string
	values?: Record<string, string | number>
}

export type LobbyInput = {
	phase: LobbyMoment
	role: LobbyRole
	/** The training's name. */
	subject?: string
	/** The day's planned hours (`13:25–16:25`), when the plan has both ends. */
	hours?: string
	/** The lead trainer's name - what participants are told. */
	trainerName?: string
	/**
	 * The planned start (before the start) or return (a break), when it is
	 * still ahead. A plan that is already behind us is no plan.
	 */
	plannedTime?: string
	/** The section the agenda continues with after a break. */
	nextSection?: string
	attendance?: Attendance
	presenter: PresenterStatus
	/** The script has items, so entering the room starts its first one. */
	agendaHasItems: boolean
}

export type LobbyFact = {
	id: 'led-by' | 'time' | 'after-break'
	message: Message
}

export type LobbyStepId = 'present' | 'join' | 'start'

export type LobbyStep = {
	id: LobbyStepId
	title: Message
	description: Message
	done: boolean
	/** Under the description, where the step is about people arriving. */
	attendance?: Message
}

/** The one thing the trainer can do from the lobby. */
export type LobbyAction = {
	id: 'start' | 'end-break'
	label: Message
}

export type LobbyView = {
	illustration: 'not-started' | 'break'
	eyebrow: Message
	heading: Message
	/** Under the heading, the training and, when the plan says, its hours. */
	meta: { subject?: string; hours?: string }
	facts: LobbyFact[]
	/** Only the trainer before the start has a checklist. */
	steps: LobbyStep[]
	/** Standing alone where there is no step to hold it (the trainer's break). */
	attendance?: Message
	/** What participants are waiting for; a live region on screen. */
	status?: Message
	action?: LobbyAction
}

const message = (
	key: string,
	values?: Record<string, string | number>
): Message => (values ? { key, values } : { key })

/**
 * `13:25–16:25`. Only a plan with both ends has hours: a start alone is the
 * "Starts at" line, and an end the backend never sent (the script fills in
 * 23:59) must not be read as one.
 */
export const hoursRange = (
	start: Date | undefined,
	end: Date | undefined,
	formatTime: (date: Date) => string
): string | undefined =>
	start && end ? `${formatTime(start)}–${formatTime(end)}` : undefined

/** The hours of the script's current day: the chunk from its start to its end. */
export const chunkHours = (
	chunk: SessionChunk | undefined,
	formatTime: (date: Date) => string
): string | undefined => {
	if (!chunk) {
		return undefined
	}
	const start = new Date(chunk.startDate)
	start.setHours(chunk.startTime.hours, chunk.startTime.minutes, 0, 0)
	return hoursRange(start, chunkEnd(chunk), formatTime)
}

/**
 * How many are in the room. "3 of 5" while the count is a fraction of the
 * invited; when more people are in than were invited (a link passed on) or
 * nobody was invited, only the number is honest. Nothing at all when there is
 * no one to count.
 */
export const attendanceMessage = (
	attendance: Attendance | undefined
): Message | undefined => {
	if (!attendance) {
		return undefined
	}
	const { present, expected } = attendance
	if (expected > 0 && present <= expected) {
		return message('in_person.lobby.attendance', { present, expected })
	}
	return present > 0
		? message('in_person.lobby.attendance_open', { present })
		: undefined
}

const stepsFor = ({
	presenter,
	attendance,
	agendaHasItems,
}: Pick<
	LobbyInput,
	'presenter' | 'attendance' | 'agendaHasItems'
>): LobbyStep[] => {
	const steps: LobbyStep[] = []
	if (presenter !== 'unavailable') {
		const done = presenter === 'running'
		steps.push({
			id: 'present',
			title: message('in_person.lobby.step.present.title'),
			description: message(
				done
					? 'in_person.lobby.step.present.description_done'
					: 'in_person.lobby.step.present.description'
			),
			done,
		})
	}
	steps.push({
		id: 'join',
		title: message('in_person.lobby.step.join.title'),
		description: message('in_person.lobby.step.join.description'),
		done: false,
		attendance: attendanceMessage(attendance),
	})
	steps.push({
		id: 'start',
		title: message('in_person.lobby.step.start.title'),
		description: message(
			agendaHasItems
				? 'in_person.lobby.step.start.description'
				: 'in_person.lobby.step.start.description_no_agenda'
		),
		done: false,
	})
	return steps
}

const notStarted = (input: LobbyInput): LobbyView => {
	const { role, subject, hours, trainerName, plannedTime } = input
	if (role === 'trainer') {
		return {
			illustration: 'not-started',
			eyebrow: message('in_person.lobby.eyebrow.before_start'),
			heading: message('in_person.lobby.heading.ready'),
			meta: { subject, hours },
			// The hours already say when; the plan's start alone fills in for them.
			facts:
				!hours && plannedTime
					? [
							{
								id: 'time',
								message: message('in_person.lobby.starts_at', {
									time: plannedTime,
								}),
							},
					  ]
					: [],
			steps: stepsFor(input),
			action: {
				id: 'start',
				label: message('in_person.lobby.action.start'),
			},
		}
	}

	const facts: LobbyFact[] = []
	if (trainerName) {
		facts.push({
			id: 'led-by',
			message: message('in_person.lobby.led_by', { name: trainerName }),
		})
	}
	if (plannedTime) {
		facts.push({
			id: 'time',
			message: message('in_person.lobby.starts_at', { time: plannedTime }),
		})
	}
	return {
		illustration: 'not-started',
		eyebrow: message('in_person.lobby.eyebrow.soon'),
		heading: message('in_person.lobby.heading.starting'),
		meta: { subject },
		facts,
		steps: [],
		status: message('in_person.lobby.status.not_started'),
	}
}

const onBreak = (input: LobbyInput): LobbyView => {
	const { role, subject, plannedTime, nextSection, attendance } = input
	const facts: LobbyFact[] = []
	if (nextSection) {
		facts.push({
			id: 'after-break',
			message: message('in_person.lobby.after_break', { title: nextSection }),
		})
	}
	if (plannedTime) {
		facts.push({
			id: 'time',
			message: message('in_person.lobby.back_at', { time: plannedTime }),
		})
	}
	const base = {
		illustration: 'break' as const,
		eyebrow: message('in_person.lobby.eyebrow.break'),
		heading: message('in_person.lobby.heading.break'),
		meta: { subject },
		facts,
		steps: [],
	}
	return role === 'trainer'
		? {
				...base,
				attendance: attendanceMessage(attendance),
				action: {
					id: 'end-break',
					label: message('in_person.lobby.action.end_break'),
				},
		  }
		: { ...base, status: message('in_person.lobby.status.break') }
}

export const lobbyView = (input: LobbyInput): LobbyView =>
	input.phase === 'not_started' ? notStarted(input) : onBreak(input)
